import { AccountInfo } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { CoherenceHeader } from "@coherence-design-system/controls";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ModalType } from "../common/commonConstants";
import { changeUpdateCafeMode, updateCafe } from "../reducers/diningReducer";
import { useAppDispatch } from "../store/store";
import styles from "../styles/header.module.css";
import { useRestoreFocusOnDialogClose } from "./DialogBox";
import HelpSettings from "./HelpSettings";
import UpdateCafe from "./UpdateCafe/UpdateCafe";
import { UpdateCafeFormFields } from "./UpdateCafe/UpdateCafeForm";

export default () => {
  const dispatch = useAppDispatch();
  const { instance, accounts } = useMsal();
  const account: AccountInfo | null = accounts[0] || {};
  const [resizeTrigger, setResizeTrigger] = useState(0);
  // UpdateCafeForm handles failed-submit focus in visual rather than registration order.
  const methods = useForm<UpdateCafeFormFields>({ mode: "all", shouldFocusError: false });
  const captureTrigger = useRestoreFocusOnDialogClose(ModalType.CONFIRM_DELETE_STATION);

  // If you modify this, make the same changes in WrappedUpdateCafeForTesting.tsx to
  // insure that UpdateCafe tests work.
  const onUpdateCafeSubmit = (data: UpdateCafeFormFields) => {
    dispatch(updateCafe(data));
    dispatch(changeUpdateCafeMode("Add"));
  };

  // Below code is to trigger a re-render of the component when the window is resized.
  // This is needed because the CoherenceHeader component does not re-render on
  // its own when the window is resized, which can cause issues with the layout of the header.
  useEffect(() => {
    const handleResize = () => setResizeTrigger((previous) => previous + 1);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <FormProvider {...methods}>
      <CoherenceHeader
        headerLabel="Chef's Menu"
        key={resizeTrigger}
        appNameSettings={{
          label: "Chef's Menu",
          ariaLabel: "Chef's Menu home page",
        }}
        farRightSettings={{
          profileSettings: {
            panelSettings: {
              emailAddress: account ? account.username : "",
              fullName: account ? account.name : "",
              imageUrl: undefined,
              onViewAccount: () => console.log("Handled the profile"),
              onLogOut: () => instance.logoutRedirect(),
              logOutLink: "",
            },
          },

          settingsSettings: account
            ? {
                panelSettings: {
                  closeButtonAriaLabel: "Close panel",
                  body: <UpdateCafe />,
                  className: styles.settings,
                  onRenderFooter: {
                    primaryButton: {
                      text: "Save",
                      onAction: () => {
                        // After dialog closes, focus should go to the panel's close button,
                        // not the Save button, because the form resets to "Add" mode.
                        const panelCloseButton = document.querySelector(
                          ".ms-Panel-closeButton, [aria-label='Close panel']",
                        ) as HTMLElement;
                        captureTrigger(panelCloseButton ?? undefined);
                        methods.handleSubmit(onUpdateCafeSubmit)();
                      },
                    },
                    secondaryButton: {
                      text: "Cancel",
                      onAction: () => {
                        const panelCloseButton = document.getElementsByClassName(
                          "ms-PanelAction-close",
                        )[0] as HTMLElement;
                        if (panelCloseButton) {
                          panelCloseButton.setAttribute("aria-label", "Close panel");
                        }
                        panelCloseButton?.click();
                      },
                    },
                  },
                },
              }
            : undefined,

          helpSettings: account
            ? {
                panelSettings: {
                  closeButtonAriaLabel: "Close panel",
                  body: <HelpSettings />,
                  className: styles.settings,
                },
              }
            : undefined,
        }}
        onDismissOpenedPanel={() => {
          return;
        }}
      />
    </FormProvider>
  );
};
